/** What is applied to the page. */
export type Theme = 'light' | 'dark';
/** What someone chose: a theme, or "follow the machine". */
export type ThemePreference = Theme | 'system';

/** Per browser, like the language (`vht:locale`): a preference, not account data. */
export const THEME_STORAGE_KEY = 'vht:theme';

/** The query the machine answers with its own light or dark setting. */
export const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * What someone chose, or light when they chose nothing (or something unreadable).
 *
 * Light is the default on purpose, as in WLX: reading the machine straight away
 * puts everyone whose Windows is dark by IT policy into the dark theme without
 * ever having chosen it. *Systeem* is there for whoever wants exactly that.
 *
 * The inline script in `index.html` does the same before first paint;
 * `theme.test.ts` runs that script against this function.
 */
export function parsePreference(stored: string | null | undefined): ThemePreference {
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'light';
}

/** The theme a preference comes down to, given what the machine says now. */
export function resolveTheme(preference: ThemePreference, systemIsDark: boolean): Theme {
  if (preference === 'system') return systemIsDark ? 'dark' : 'light';
  return preference;
}

/** Light → dark → system → light: one menu item, three choices. */
export const NEXT_PREFERENCE: Record<ThemePreference, ThemePreference> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
};

/**
 * The portal stays light, whatever the browser chose. A partner never sees the
 * choice, and the portal is not designed in dark: an internal user who looks at
 * it in a dark backoffice browser should see what the partner sees.
 */
export function isLightOnlyPath(pathname: string): boolean {
  return pathname === '/portal' || pathname.startsWith('/portal/');
}
